import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Leaf, Flame, Wheat, ArrowLeft, CheckCircle2, UtensilsCrossed } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SuggestDishPage = () => {
  const [dishName, setDishName] = useState('');
  const [vegan, setVegan] = useState(false);
  const [glutenFree, setGlutenFree] = useState(false);
  const [spicy, setSpicy] = useState(false);
  const [email, setEmail] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!dishName.trim()) { setError('Anna ruoan nimi.'); return; }
    if (email && !gdpr) { setError('Hyväksy tietosuojaseloste jatkaaksesi.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/suggest-dish`, {
        dish_name: dishName,
        dietary: { vegan, gluten_free: glutenFree, spicy },
        email: email || undefined,
        gdpr_consent: gdpr,
      });
      setDone(true);
    } catch {
      setError('Lähetys epäonnistui. Yritä uudelleen tai soita 040 377 9200.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF6E8] via-white to-[#F5F3EF] pt-28 pb-24 md:pb-16">
      <div className="max-w-xl mx-auto px-5">
        <Link to="/menu" className="inline-flex items-center gap-2 text-[#6B705C] hover:text-[#C97A2B] text-xs tracking-wider uppercase mb-6" data-testid="back-to-menu-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Takaisin lounaslistaan
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-xl border border-[#D9D9D6] overflow-hidden">
          <div className="bg-gradient-to-r from-[#C97A2B] to-[#B65A3A] px-6 py-7 text-white">
            <div className="flex items-center gap-2 mb-1">
              <UtensilsCrossed className="w-4 h-4" />
              <p className="text-[10px] tracking-[0.3em] uppercase font-semibold">Kuuntelemme sinua</p>
            </div>
            <h1 className="text-2xl md:text-3xl leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Ehdota lempiruokaasi<br/>buffet-valikoimaan!
            </h1>
            <p className="text-white/85 text-sm mt-2">Suggest a Dish — kerro mitä haluaisit nähdä lounaslautasellasi.</p>
          </div>

          {done ? (
            <div className="p-8 text-center" data-testid="suggest-success">
              <CheckCircle2 className="w-14 h-14 text-[#C97A2B] mx-auto mb-4" />
              <h2 className="text-xl text-[#2F2F2F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>Kiitos ehdotuksestasi! 🙏</h2>
              <p className="text-sm text-[#6B705C] leading-relaxed">
                Ehdotuksesi on vastaanotettu. {email ? 'Lähetämme sinulle sähköpostia, kun ruokaa tarjoillaan.' : ''}
              </p>
              <Link to="/menu" className="inline-block mt-6 px-6 py-3 bg-[#C97A2B] text-white text-xs tracking-wider uppercase font-semibold rounded-full hover:bg-[#B65A3A] transition-colors">
                Palaa lounaslistaan
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 md:p-7 space-y-5">
              <div>
                <label className="block text-[11px] tracking-widest uppercase text-[#6B705C] font-semibold mb-1.5">
                  Ruoan nimi / Dish name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" required maxLength={120}
                  value={dishName} onChange={(e) => setDishName(e.target.value)}
                  placeholder="esim. Mango Chicken Masala"
                  className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] placeholder-[#6B705C]/50 focus:outline-none focus:border-[#C97A2B] focus:bg-white transition-colors text-sm"
                  data-testid="suggest-dish-name"
                />
              </div>

              <div>
                <label className="block text-[11px] tracking-widest uppercase text-[#6B705C] font-semibold mb-2">
                  Erityisruokavalio
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { k: 'vegan', state: vegan, set: setVegan, label: 'Vegaaninen', Icon: Leaf, color: 'text-green-600' },
                    { k: 'gluten', state: glutenFree, set: setGlutenFree, label: 'Gluteeniton', Icon: Wheat, color: 'text-amber-600' },
                    { k: 'spicy', state: spicy, set: setSpicy, label: 'Tulinen', Icon: Flame, color: 'text-red-500' },
                  ].map(({ k, state, set, label, Icon, color }) => (
                    <button key={k} type="button" onClick={() => set(!state)}
                      data-testid={`suggest-tag-${k}`}
                      className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-semibold border transition-all ${
                        state ? 'bg-[#C97A2B]/10 border-[#C97A2B] text-[#2F2F2F]' : 'bg-white border-[#D9D9D6] text-[#6B705C] hover:border-[#C97A2B]/50'
                      }`}>
                      <Icon className={`w-4 h-4 ${state ? color : 'opacity-60'}`} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[11px] tracking-widest uppercase text-[#6B705C] font-semibold mb-1.5">
                  Sähköposti (vapaaehtoinen)
                </label>
                <input
                  type="email" maxLength={120}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="sinun@sähköposti.fi"
                  className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] placeholder-[#6B705C]/50 focus:outline-none focus:border-[#C97A2B] focus:bg-white transition-colors text-sm"
                  data-testid="suggest-email"
                />
                <p className="text-[11px] text-[#C97A2B] mt-1.5">💌 Saat ilmoituksen, kun ruoka tarjoillaan!</p>
              </div>

              {email && (
                <label className="flex items-start gap-2 text-xs text-[#6B705C] cursor-pointer">
                  <input type="checkbox" checked={gdpr} onChange={(e) => setGdpr(e.target.checked)} className="mt-0.5" data-testid="suggest-gdpr" />
                  <span>Hyväksyn, että sähköpostiani käytetään vain tähän ehdotukseen liittyvään yhteydenottoon.</span>
                </label>
              )}

              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{error}</p>}

              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-[#C97A2B] text-white rounded-full text-sm font-bold tracking-wider uppercase hover:bg-[#B65A3A] transition-colors disabled:opacity-50"
                data-testid="suggest-submit-btn">
                {loading ? 'Lähetetään…' : (<>Lähetä ehdotus <Send className="w-4 h-4" /> 🚀</>)}
              </button>

              <p className="text-[11px] text-[#6B705C]/70 text-center">
                Ehdotuksesi lähetetään suoraan keittiöllemme. Ei äänestystä, ei julkista listaa — vain hyviä ideoita.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SuggestDishPage;
